import { useMemo } from 'react'
import { EstimatedPaymentSchedule, LineItem, LineItemType, PaymentCategoryV2, PaymentStatusFull } from '@genoa/domain'
import { PaymentSchedule } from '@genoa/middle-end'
import { getDateTimeFromISO, ordinalSuffixFor, toFormattedDollars } from '@genoa/utils'

import { useSecureLineOfCredit } from '../../../../../../hooks'
import { usePropertyPassthroughFee } from '../../../../../../hooks/flex2/pricing/use-property-passthrough-fee'
import { PaymentLineItemProps } from '../../../../../components/PaymentSchedule/PaymentLineItem'

export interface PaymentScheduleItemProps {
  readonly label: string
  readonly amount: string
  readonly lineItems?: readonly PaymentLineItemProps[]
}

const handlePaymentDate = (payment: PaymentStatusFull) => {
  switch (payment.category) {
    case PaymentCategoryV2.DEPOSIT:
      return 'Today'
    default:
      if (payment.due_date) {
        const date = getDateTimeFromISO(payment.due_date)
        return `${ordinalSuffixFor(date.day)} of the month`
      }
      return payment.display_title
  }
}

export const getPropertyPassthroughFeeFromPaymentStatus = (payments: PaymentStatusFull[] | undefined) => {
  payments?.forEach((p) =>
    p.line_items?.forEach((item) => {
      if (item.line_item_type === LineItemType.PROPERTY_PASSTHROUGH_FEE) {
        return item.amount_cents
      }
    })
  )
  return 0
}

export const getPropertyPassthroughFeeFromPaymentSchedule = (
  payments: (PaymentSchedule | EstimatedPaymentSchedule)[] | undefined
) => {
  return payments?.find((p) => !!p.property_passthrough_fee_amount)?.property_passthrough_fee_amount || 0
}

export const usePaymentStatusListToSchedule = (payments: PaymentStatusFull[] | undefined) => {
  const downpayment = payments?.find((payment) =>
    [PaymentCategoryV2.DOWN_PAYMENT, PaymentCategoryV2.DEPOSIT].includes(payment.category)
  )
  const repayments = payments?.filter((payment) => payment.category === PaymentCategoryV2.REPAYMENT)
  const membershipFee = payments?.find((payment) => payment.category === PaymentCategoryV2.MMF)

  return usePaymentStatusToSchedule(downpayment, repayments, membershipFee)
}

export const usePaymentStatusToSchedule = (
  firstPayment: PaymentStatusFull | undefined,
  repayments: PaymentStatusFull[] | undefined,
  membershipPayment?: PaymentStatusFull | undefined
) => {
  const { isSimplifiedPaymentsEnabled, slcType } = useSecureLineOfCredit()
  const propertyPassthroughFee = getPropertyPassthroughFeeFromPaymentStatus(repayments || [])
  const { propertyPassthroughFeeLineItem, displayPropertyPassthroughFee, displayPropertyPassthroughFeeModal } =
    usePropertyPassthroughFee(propertyPassthroughFee)

  const formatLineItems = (lineItems: LineItem[]) => {
    return lineItems.map((lineItem) => {
      const moreInfo =
        lineItem.line_item_type === LineItemType.PROPERTY_PASSTHROUGH_FEE
          ? displayPropertyPassthroughFeeModal
          : undefined
      return {
        label: lineItem.display_title,
        amount: toFormattedDollars(lineItem.amount_cents),
        moreInfo: moreInfo,
      }
    }) as PaymentLineItemProps[]
  }

  return useMemo(() => {
    if (firstPayment === undefined || repayments === undefined) {
      return []
    }

    const firstPaymentScheduleItem: PaymentScheduleItemProps = {
      label: handlePaymentDate(firstPayment),
      amount: toFormattedDollars(firstPayment.total_amount_cents),
      lineItems: slcType
        ? [
            {
              label:
                firstPayment.line_items.find((lineItem) => lineItem.line_item_type === LineItemType.BASE)
                  ?.display_title || '',
            },
          ]
        : formatLineItems(firstPayment?.line_items),
    }

    const repaymentScheduleItems: PaymentScheduleItemProps[] = repayments.map((repayment) => ({
      label: handlePaymentDate(repayment),
      amount: toFormattedDollars(repayment.total_amount_cents),
      lineItems: slcType
        ? [
            {
              label: repayment.display_title,
              amount: toFormattedDollars(
                repayment.line_items.find((lineItem) => lineItem.line_item_type === LineItemType.BASE)?.amount_cents
              ),
            },
            {
              label:
                repayment.line_items.find((lineItem) => lineItem.line_item_type === LineItemType.FINANCE_CHARGE)
                  ?.display_title || '',
              amount: toFormattedDollars(
                repayment.line_items.find((lineItem) => lineItem.line_item_type === LineItemType.FINANCE_CHARGE)
                  ?.amount_cents
              ),
            },
            ...(displayPropertyPassthroughFee ? [propertyPassthroughFeeLineItem] : []),
          ]
        : formatLineItems(repayment?.line_items),
    }))

    const membershipPaymentItem: PaymentScheduleItemProps | undefined = {
      label: membershipPayment?.display_title || '',
      amount: toFormattedDollars(membershipPayment?.total_amount_cents) || '',
      lineItems: membershipPayment?.line_items ? formatLineItems(membershipPayment?.line_items) : [],
    }

    if (membershipPayment) {
      return [firstPaymentScheduleItem, ...repaymentScheduleItems, membershipPaymentItem]
    } else {
      return [firstPaymentScheduleItem, ...repaymentScheduleItems]
    }
  }, [firstPayment, repayments, isSimplifiedPaymentsEnabled])
}
